import { useEffect, useState } from "react";
import api from "../lib/api";
import DigestSalesManagers from "./DigestSalesManagers";
import { Mail, Trash2, Plus, Send, Clock, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Admin-only card for the Weekly YTD digest subscribers.
 *
 * Shows the current list, lets admins add/remove emails, triggers a
 * "send test" (goes to the authenticated admin's own inbox so we never
 * build an open relay), and surfaces the last cron run audit so you
 * can see when Monday's send actually went out.
 */
export default function WeeklyDigestCard() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail]     = useState("");
  const [name, setName]       = useState("");
  const [adding, setAdding]   = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTR]   = useState(null);
  const [err, setErr]         = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/digest/subscribers")
       .then((r) => setData(r.data))
       .catch((e) => setErr(e?.response?.data?.detail || "Error cargando lista"))
       .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onAdd = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setAdding(true); setErr(null);
    try {
      await api.post("/digest/subscribers", { email: email.trim(), name: name.trim() || null });
      setEmail(""); setName("");
      load();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "No se pudo agregar el suscriptor");
    } finally {
      setAdding(false);
    }
  };

  const onRemove = async (id, emailLabel) => {
    if (!window.confirm(`Retirar ${emailLabel} de la lista?`)) return;
    try {
      await api.delete(`/digest/subscribers/${id}`);
      load();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "No se pudo retirar");
    }
  };

  const onSendTest = async () => {
    setTesting(true); setTR(null); setErr(null);
    try {
      const r = await api.post("/digest/send-test");
      setTR(r.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "No se pudo enviar la prueba");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="surface p-5 mb-5" data-testid="weekly-digest-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ width: 36, height: 36, background: "#dbeafe", color: "#2563eb" }}>
          <Mail size={18}/>
        </div>
        <div className="flex-1">
          <h3 className="text-base font-extrabold font-display">Resumen Semanal YTD por Email</h3>
          <p className="text-sm text-gray-500 font-semibold mt-0.5">
            Reporte automático de KPIs, Funnel, Top 5 Canales y Top 3 Sucursales.
            {data?.schedule ? (
              <span className="inline-flex items-center gap-1 ml-2 pill" style={{ background: "#eff6ff", color: "#1e40af" }}>
                <Clock size={10}/> {data.schedule}
              </span>
            ) : null}
          </p>
        </div>
        <button
          className="btn flex items-center gap-2"
          onClick={onSendTest}
          disabled={testing}
          data-testid="digest-send-test-btn"
          title="Envía el resumen YTD a tu correo de admin para previsualizarlo">
          <Send size={14}/> {testing ? "Enviando..." : "Enviar prueba"}
        </button>
      </div>

      {err && (
        <div className="rounded-lg p-2 mb-3 flex items-center gap-2 text-xs font-semibold"
             style={{ background: "#fee2e2", color: "#991b1b" }}>
          <AlertCircle size={12}/> {err}
        </div>
      )}
      {testResult?.sent && (
        <div className="rounded-lg p-2 mb-3 flex items-center gap-2 text-xs font-semibold"
             style={{ background: "#dcfce7", color: "#166534" }}>
          <CheckCircle2 size={12}/> Correo de prueba enviado a <strong>{testResult.to}</strong>
          · Leads semana {Number(testResult.summary_preview?.wow?.cur?.leads || 0).toLocaleString("es-DO")}
          · Ventas semana {Number(testResult.summary_preview?.wow?.cur?.units || 0).toLocaleString("es-DO")}
        </div>
      )}

      {/* Add form */}
      <form onSubmit={onAdd} className="flex gap-2 flex-wrap items-end mb-4" data-testid="digest-add-form">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="nombre@sdm.com.do"
            className="w-full border rounded-lg px-3 py-2 text-sm font-semibold"
            style={{ borderColor: "#e2e8f0" }}
            data-testid="digest-email-input"
          />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">Nombre (opcional)</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Gerente de Ventas"
            className="w-full border rounded-lg px-3 py-2 text-sm font-semibold"
            style={{ borderColor: "#e2e8f0" }}
            data-testid="digest-name-input"
          />
        </div>
        <button type="submit" className="btn primary flex items-center gap-2" disabled={adding}
                data-testid="digest-add-btn">
          <Plus size={14}/> {adding ? "Agregando..." : "Agregar"}
        </button>
      </form>

      <DigestSalesManagers onChanged={load}/>

      {/* List */}
      {loading && <div className="text-xs text-gray-400 font-semibold">Cargando...</div>}
      {!loading && data?.subscribers?.length === 0 && (
        <div className="text-xs text-gray-500 font-semibold py-2">
          Aún no hay suscriptores. Añade uno arriba — el próximo lunes a las 9:00 AM lo recibirá automáticamente.
        </div>
      )}
      {!loading && data?.subscribers?.length > 0 && (
        <div className="rounded-lg overflow-hidden" style={{ border: "1px solid #e2e8f0" }}>
          <table className="w-full text-sm" data-testid="digest-subscribers-table">
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-extrabold text-gray-500">Email</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-extrabold text-gray-500">Nombre</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-extrabold text-gray-500"></th>
              </tr>
            </thead>
            <tbody>
              {data.subscribers.map((s) => (
                <tr key={s.id} style={{ borderTop: "1px solid #e2e8f0" }}>
                  <td className="px-3 py-2 font-semibold">{s.email}</td>
                  <td className="px-3 py-2 text-gray-500">{s.name || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      className="btn text-red-600 flex items-center gap-1 ml-auto"
                      style={{ fontSize: 11, padding: "4px 8px" }}
                      onClick={() => onRemove(s.id, s.email)}
                      data-testid={`digest-remove-${s.email}`}>
                      <Trash2 size={11}/> Retirar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Last run audit */}
      {data?.last_run && (
        <div className="mt-4 p-3 rounded-lg text-xs font-semibold flex items-start gap-2"
             style={{ background: "#f1f5f9", color: "#334155" }}
             data-testid="digest-last-run">
          <Clock size={12} style={{ marginTop: 2 }}/>
          <div>
            Último envío: <strong>{new Date(data.last_run.ran_at).toLocaleString("es-DO")}</strong>
            {" · "}
            {data.last_run.sent?.length || 0} entregado(s)
            {data.last_run.failed?.length > 0 && (
              <span style={{ color: "#991b1b" }}> · {data.last_run.failed.length} fallido(s)</span>
            )}
            {data.last_run.snapshot && (
              <span className="text-gray-500 font-normal">
                {" · "}Snapshot: Leads {Number(data.last_run.snapshot.ytd_leads).toLocaleString("es-DO")} · Ventas {Number(data.last_run.snapshot.ytd_units).toLocaleString("es-DO")}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
